<template>
  <div>
    <ToastPrime position="bottom-left" />

    <div class="card-body pt-0 px-0" style="max-height: 400px; overflow-y: auto;"
      :class="{ 'form-disabled': readonly }">
      <DataTable :value="isHeaderLoading ? dummyRows : rows" class="p-datatable-sm p-datatable-gridlines" scrollable
        table-style="min-width: 100rem">

        <Column v-for="col in renderColumns" :key="col.key"
          :header-class="col.isOld ? 'text-center old-value-col' : 'bg-light text-center'"
          :body-class="col.isOld ? 'old-value-body' : ''"
          :header-style="{ minWidth: col.width + 'px' }" :body-style="{ minWidth: col.width + 'px' }">
          <template #header>
            {{ col.label }}
            <span v-if="!col.isOld && rows.some(r => isColumnMandatoryS(r, col.key))" class="text-danger">*</span>
          </template>
          <template #body="{ data }">
            <Skeleton v-if="isHeaderLoading" />
            <template v-else>
              <!-- Material Number — search-as-you-type; kolom turunan baru aktif setelah benar-benar dipilih -->
              <AutoComplete v-if="col.kind === 'material'" v-model="data.material_number"
                :suggestions="optionsFor(data, 'material_number')" option-label="label" option-value="value"
                :force-selection="false" size="small" placeholder="Search Material Number..." :min-length="2"
                :delay="1000" show-clear :disabled="!isColumnEnabledS(data, 'material_number')"
                :loading="columnLoadingS(data, 'material_number')"
                :invalid="isColumnInvalidFn(data, 'material_number')" fluid
                @complete="e => searchMaterial(e, data)" @item-select="e => onMaterialNumberSelect(data, e)"
                @clear="onMaterialClear(data)">
                <template #option="{ option }">
                  <div>
                    <div style="font-size:0.85rem;font-weight:600">{{ option.label }}</div>
                    <div style="font-size:0.75rem;color:#6b7280">{{ option.short_text }}</div>
                  </div>
                </template>
              </AutoComplete>

              <!-- Nilai lama / field read-only (material_type) — selalu mati, label dari config -->
              <InputText v-else-if="col.isOld || col.kind === 'readonly'" :model-value="displayValue(data, col)"
                size="small" placeholder="-" disabled fluid />

              <!-- Select dari data_source (array / detail_array / endpoint) — engine yang load opsinya -->
              <SelectPrime v-else-if="col.kind === 'select'" v-model="data[col.key]" :options="optionsFor(data, col.key)"
                option-label="label" option-value="value" placeholder="Select"
                :disabled="!isColumnEnabledS(data, col.key) || isColumnLockedS(data, col.key)" :loading="columnLoadingS(data, col.key)"
                :invalid="isColumnInvalidFn(data, col.key)" fluid
                @show="onColumnShow(data, col.key)" @update:model-value="v => onColumnChange(data, col.key, v)" />

              <!-- Field numeric (rules: numeric di config) -->
              <InputNumber v-else-if="col.kind === 'number'" v-model="data[col.key]" size="small" placeholder="0"
                :use-grouping="false" :min="0" :max-fraction-digits="0"
                :disabled="!isColumnEnabledS(data, col.key)" :invalid="isColumnInvalidFn(data, col.key)" fluid
                @update:model-value="v => onColumnChange(data, col.key, v)" />

              <!-- Sisanya text biasa -->
              <InputText v-else v-model="data[col.key]" size="small"
                :disabled="!isColumnEnabledS(data, col.key)" :invalid="isColumnInvalidFn(data, col.key)" fluid />
            </template>
          </template>
        </Column>

        <Column header-class="bg-light text-center" body-class="text-center"
          :header-style="{ width: '70px', minWidth: '70px' }" :body-style="{ width: '70px', minWidth: '70px' }">
          <template #header>
            <div class="centered-column-header">#</div>
          </template>
          <template #body="{ index }">
            <Skeleton v-if="isHeaderLoading" />
            <ButtonPrime v-else icon="pi pi-trash" size="small" severity="danger" outlined rounded
              :disabled="readonly || rows.length <= 1" @click="removeRow(index)" />
          </template>
        </Column>

        <template #empty>
          <div style="text-align:center;padding:40px 0">
            <img src="/no_data.png" alt="No Data" style="max-width:330px;opacity:0.7" />
            <div style="color:#888;font-size:1.1em;font-weight:500">No Data</div>
          </div>
        </template>
      </DataTable>
    </div>

    <!-- Dialog Tracking & Import Excel digerakkan parent (New_McrItem) lewat toolbar di sana —
         jangan pasang instance kedua di sini. -->
  </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { mapState } from 'vuex';
import advanceSearchService from '@/services/generalLayout/advanceSearchService';
import formConfigHelper from '@/helpers/New_formConfig';
import materialNumberFormatter from '@/helpers/materialNumberFormatter';
import api from '@/helpers/api';
import useFormConfigEngine from '@/composables/useFormConfigEngine';

// Tab & field repeatable di config FORM06 (MASS UPDATE). Backend bisa menulis nama tab
// dengan pemisah lain, jadi key-nya dinormalkan dulu sebelum dicocokkan.
const MASS_UPDATE_TAB = 'mass_update';
const normalizeTabKey = (key) => String(key).toLowerCase().replace(/[^a-z0-9]+/g, '_');

// URUTAN kolom = layout hardcode (config cuma atur behavior). Urutan TIDAK diambil dari
// urutan key config: `fields` disimpan di kolom jsonb PostgreSQL yang tidak menjaga
// urutan key, jadi urutannya acak. Kolom yang ada di config tapi tidak terdaftar di
// sini tetap dirender, ditaruh di belakang.
const COLUMN_ORDER = [
  'material_number', 'material_type', 'plant',
  'mu_mrp_type', 'mu_mrp_group', 'mu_reorder_point', 'mu_lot_size', 'mu_abc_indicator',
  'mu_min_lot_size', 'mu_max_lot_size', 'mu_max_stock', 'mu_rounding_value',
  'mu_special_procurement', 'mu_unit_price', 'mu_safety_stock', 'mu_planned_delivery_time',
  'mu_material_freight_group', 'mu_storage_location', 'mu_bin_location', 'mu_uom',
];

// Label kolom (layout hardcode — config cuma atur behavior, bukan label). Key = nama kolom config.
const COLUMN_LABELS = {
  material_number: 'Material Number',
  material_type: 'Material Type',
  plant: 'Plant',
  cat_industry_sector: 'Industry Sector',
  mu_mrp_type: 'MRP Type',
  mu_mrp_group: 'MRP Group',
  mu_reorder_point: 'Reorder Point',
  mu_lot_size: 'Lot Size',
  mu_abc_indicator: 'ABC Indicator',
  mu_min_lot_size: 'Minimum Lot Size',
  mu_max_lot_size: 'Maximum Lot Size',
  mu_max_stock: 'Maximum Stock Level',
  mu_rounding_value: 'Rounding Value',
  mu_special_procurement: 'Special Procurement',
  mu_unit_price: 'Unit Price',
  mu_safety_stock: 'Safety Stock',
  mu_planned_delivery_time: 'Planned Delivery Time',
  mu_material_freight_group: 'Material Freight Group',
  mu_storage_location: 'Storage Location',
  mu_bin_location: 'Bin Location',
  mu_uom: 'UOM',
};

// Kolom dataset besar: opsinya diambil saat user mengetik (server-side search), bukan di-preload.
const SEARCH_AS_YOU_TYPE_COLUMNS = new Set(['material_number']);

// Key warisan FE di item store (wadah opsi versi lama) — dibuang supaya tidak bentrok.
const LEGACY_ROW_KEYS = ['options', 'loading', 'loaded', 'plantOptions', 'materialLookupDetail'];
// State visual per baris yang tidak boleh ikut ter-submit.
const VISUAL_STATE_KEYS = ['columnOptions', 'columnLoading', 'columnLoaded', 'rowErrors'];
// Kolom turunan yang dihitung backend — tidak pernah dikirim FE.
const SERVER_COMPUTED_KEYS = ['mu_inventory_value'];

export default {
  name: 'MassUpdateForm',
  props: {
    readonly: { type: Boolean, default: false }
  },
  setup(props) {
    const vm = getCurrentInstance().proxy;
    const rowContext = { row: null, column: null };
    const bucketKey = (column) => column || '__row';

    const storeFor = (row, column) => {
      if (!row) return { options: {}, loading: {}, loaded: {} };
      const key = bucketKey(column);
      row.columnOptions = row.columnOptions || {};
      row.columnLoading = row.columnLoading || {};
      row.columnLoaded = row.columnLoaded || {};
      row.columnOptions[key] = row.columnOptions[key] || {};
      row.columnLoading[key] = row.columnLoading[key] || {};
      row.columnLoaded[key] = row.columnLoaded[key] || {};
      return {
        options: row.columnOptions[key],
        loading: row.columnLoading[key],
        loaded: row.columnLoaded[key],
      };
    };
    const readStore = (row, column) => {
      const key = bucketKey(column);
      return {
        options: row?.columnOptions?.[key] || {},
        loading: row?.columnLoading?.[key] || {},
        loaded: row?.columnLoaded?.[key] || {},
      };
    };

    const materialDetails = new WeakMap();
    const materialSelected = new WeakSet();

    const engine = useFormConfigEngine({
      vm,
      getFormConfig: () => vm.formConfig,
      getDialog: () => {
        const store = storeFor(rowContext.row, rowContext.column);
        return {
          form: rowContext.row || {},
          options: store.options,
          loading: store.loading,
          loaded: store.loaded,
          errors: (rowContext.row && rowContext.row.rowErrors) || {},
        };
      },
      getMcrType: () => vm.mcrHeader?.type || '',
      isView: () => props.readonly,
      getCtx: () => vm.fieldCtxS(rowContext.row),
      getDetailSources: () => ({ materialLookupDetail: materialDetails.get(rowContext.row) }),
      searchAsYouTypeFields: SEARCH_AS_YOU_TYPE_COLUMNS,
    });

    return { ...engine, rowContext, storeFor, readStore, materialDetails, materialSelected };
  },
  data() {
    return {
      rows: [],
      dummyRows: Array.from({ length: 3 }, () => ({})),
      formConfig: null,
      companyCode: '',
      clientCode: ''
    };
  },
  computed: {
    ...mapState({
      isHeaderLoading: state => state.mcr.isLoading.header,
      mcrHeader: state => state.mcr.mcrHeader,
      allMcrItem: state => state.mcr.allMcrItem || [],
      mcrDetail: state => state.mcr.mcrDetail,
    }),
    tabKey() {
      return this.resolveTabKey(this.formConfig);
    },
    tabFields() {
      return formConfigHelper.getTabFieldMap(this.formConfig, this.tabKey) || null;
    },
    gridField() {
      return this.resolveGridField(this.formConfig, this.tabKey);
    },
    gridFieldConfig() {
      return this.tabFields?.[this.gridField] || null;
    },
    // Daftar kolom: yang ADA di config (config-driven behavior), tapi URUTAN-nya dari
    // COLUMN_ORDER (layout). Kalau config belum termuat, pakai COLUMN_ORDER apa adanya.
    columns() {
      const configColumns = this.resolveColumns(this.formConfig);
      if (!configColumns.length) return [...COLUMN_ORDER];
      // Urutkan sesuai layout; kolom config yang tidak terdaftar tetap ikut di belakang.
      const ordered = COLUMN_ORDER.filter(code => configColumns.includes(code));
      const extra = configColumns.filter(code => !COLUMN_ORDER.includes(code));
      return [...ordered, ...extra];
    },
    // Baris render = tiap kolom config, dipasangkan kolom "Old <label>" read-only
    // (mu_old_*) di depannya. Pasangan old cuma layout — tidak ada di config.
    renderColumns() {
      const out = [];
      this.columns.forEach(code => {
        // Saat header masih loading belum ada baris untuk mengevaluasi visibility -
        // tampilkan kolomnya dulu supaya skeleton-nya terlihat.
        if (!this.isHeaderLoading && !this.isColumnVisibleS(code)) return;
        const kind = this.columnMeta(code);
        const width = kind === 'material' ? 220 : kind === 'select' ? 200 : kind === 'number' ? 140 : 160;
        const oldKey = this.oldKeyOf(code);
        if (oldKey) {
          out.push({
            key: oldKey, label: `Old ${this.columnLabel(code)}`, kind: 'old',
            isOld: true, sourceField: code, numeric: kind === 'number', width: 140,
          });
        }
        out.push({ key: code, label: this.columnLabel(code), kind, isOld: false, width });
      });
      return out;
    },
  },
  watch: {
    'mcrDetail.formConfig': {
      immediate: true,
      handler(val) {
        this.formConfig = val || null;
      },
    },
    formConfig() {
      this.rows.forEach(row => this.refreshRow(row));
    },
    allMcrItem: {
      immediate: true,
      handler(val) {
        // Mass update TIDAK punya baris kosong default (Add disembunyikan; baris datang dari
        // Import). Kalau belum ada data, `rows` dibiarkan kosong supaya template `#empty`
        // menampilkan "No Data", bukan tabel dengan satu baris kosong.
        const base = (val && val.length > 0) ? [...val] : [];
        this.rows = base.map(item => this.normalizeRow(item));
        this.rows.forEach(row => {
          row.material_number_raw = row.material_number_raw || row.material_number || '';
          if (row.material_number_raw) {
            row.material_number = materialNumberFormatter.formatMaterialNumber(row.material_number_raw);
            this.materialSelected.add(row);
          }
          this.refreshRow(row);
          if (row.material_number_raw) {
            this.fetchMaterialDetail(row, row.material_number_raw);
          }
        });
      }
    }
  },
  mounted() {
    const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
    this.companyCode = userAuth?.user?.company_code || '';
    this.clientCode = userAuth?.user?.client_code || '';
  },
  methods: {
    // ---------------------------------------------------------------------
    // Config
    // ---------------------------------------------------------------------
    resolveTabKey(config) {
      const keys = Object.keys(config?.tab_fields || {});
      if (keys.includes(MASS_UPDATE_TAB)) return MASS_UPDATE_TAB;
      const matched = keys.find(key => normalizeTabKey(key) === MASS_UPDATE_TAB);
      if (matched) return matched;
      if (keys.length === 1) return keys[0];
      let best = MASS_UPDATE_TAB;
      let bestScore = 0;
      keys.forEach(key => {
        const score = this.resolveColumnsOfTab(config, key).length;
        if (score > bestScore) {
          bestScore = score;
          best = key;
        }
      });
      return best;
    },
    resolveGridField(config, tabKey) {
      const fields = formConfigHelper.getTabFieldMap(config, tabKey) || {};
      if (fields[MASS_UPDATE_TAB]?.columns) return MASS_UPDATE_TAB;
      return Object.keys(fields).find(name => fields[name]?.columns) || MASS_UPDATE_TAB;
    },
    resolveColumnsOfTab(config, tabKey) {
      const columns = formConfigHelper.getTabFieldMap(config, tabKey)?.[this.resolveGridField(config, tabKey)]?.columns;
      return columns && typeof columns === 'object' ? Object.keys(columns) : [];
    },
    resolveColumns(config) {
      // Kolom `mu_old_*` di config diabaikan: pasangan nilai lama dibuat otomatis oleh
      // oldKeyOf() dari kolom `mu_*`, jadi kalau ikut dirender akan tampil dobel.
      return this.resolveColumnsOfTab(config, this.resolveTabKey(config))
        .filter(code => !code.startsWith('mu_old_'));
    },
    columnConfig(column) {
      return formConfigHelper.getColumnConfig(this.formConfig, this.tabKey, this.gridField, column);
    },
    columnLabel(code) {
      return COLUMN_LABELS[code] || code.replace(/^mu_(old_)?/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    // Kolom mu_* punya pasangan nilai lama mu_old_* (cuma tampilan).
    oldKeyOf(code) {
      return code.startsWith('mu_') ? 'mu_old_' + code.slice(3) : null;
    },
    // Jenis kontrol per kolom: dari config (data_source / rule numeric), bukan daftar per-field.
    columnMeta(code) {
      const cfg = this.columnConfig(code) || {};
      if (code === 'material_number') return 'material';
      if (code === 'material_type') return 'readonly';
      if (cfg.data_source) return 'select';
      const numeric = (cfg.rules || []).some(rule => rule.type === 'numeric');
      return numeric ? 'number' : 'text';
    },
    // Kolom terkunci (`readonlyandsaved`): tampil, tidak bisa diubah, nilainya tetap disimpan (mis. Industry Sector).
    isColumnLockedS(row, column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return false;
      return formConfigHelper.isColumnReadonlySaved(this.formConfig, this.tabKey, this.gridField, column, this.fieldCtxS(row));
    },
    fieldCtxS(row) {
      return {
        isView: this.readonly,
        getField: (name) => {
          if (name === 'company') return this.companyCode;
          if (name === 'client') return this.clientCode;
          if (name === 'mcr_type') return (this.mcrHeader?.type || '').toUpperCase();
          if (name === 'material_number') {
            return this.materialSelected.has(row) ? row.material_number : undefined;
          }
          return row?.[name];
        },
        getFieldOption: (field, attribute) => {
          const options = this.readStore(row, field).options[this.gridField] || [];
          const selected = options.find(o => String(o.value) === String(row?.[field]));
          return selected ? selected[attribute] : undefined;
        },
        hasLine: () => false,
      };
    },

    // ---------------------------------------------------------------------
    // Evaluasi config (ctx per baris dipasang ke rowContext sebelum evaluasi).
    // ---------------------------------------------------------------------
    activate(row, column = null) {
      this.rowContext.row = row || null;
      this.rowContext.column = column;
    },
    isColumnVisibleS(column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return true;
      // Belum ada baris: evaluasi pakai ctx form-level saja (placeholder tanpa field baris),
      // supaya HEADER tetap tampil walau body-nya "No Data" (jangan sampai kolom hilang
      // hanya karena rows kosong).
      if (!this.rows.length) return this.isColumnVisibleForRow({}, column);
      return this.rows.some(row => this.isColumnVisibleForRow(row, column));
    },
    isColumnVisibleForRow(row, column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return true;
      this.activate(row, column);
      return this.isColumnVisibleFn(column, this.gridField, this.tabKey);
    },
    isColumnEnabledS(row, column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return true;
      this.activate(row, column);
      return this.isColumnEnabledFn(column, this.gridField, this.tabKey);
    },
    isColumnMandatoryS(row, column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return false;
      this.activate(row, column);
      return this.isColumnMandatoryFn(column, this.gridField, this.tabKey);
    },
    optionsFor(row, column) {
      return this.readStore(row, column).options[this.gridField] || [];
    },
    columnLoadingS(row, column) {
      return !!this.readStore(row, column).loading[this.gridField];
    },
    // Nilai tampilan untuk kolom read-only / nilai lama. Code select di-resolve ke label
    // lewat opsi kolom barunya (opsi mu_* sudah dimuat per baris).
    displayValue(row, col) {
      const value = row?.[col.key];
      if (value === null || value === undefined || value === '') return '';
      if ((col.isOld || col.kind === 'readonly')) {
        const source = col.sourceField || col.key;
        const cfg = this.columnConfig(source) || {};
        if (col.isOld && cfg.data_source && !col.numeric) {
          return this.getSelectValueLabelFn(this.optionsFor(row, source), value);
        }
      }
      return String(value);
    },

    // ---------------------------------------------------------------------
    // Baris
    // ---------------------------------------------------------------------
    emptyRow() {
      const row = {
        material_number: '',
        material_number_raw: '',
        material_type: '',
        plant: '',
        rowErrors: {},
        columnOptions: {},
        columnLoading: {},
        columnLoaded: {},
      };
      this.columns.forEach(code => {
        if (!(code in row)) row[code] = this.columnMeta(code) === 'number' ? 0 : '';
        const oldKey = this.oldKeyOf(code);
        if (oldKey) row[oldKey] = this.columnMeta(code) === 'number' ? 0 : '';
      });
      return row;
    },
    normalizeRow(item) {
      const row = { ...this.emptyRow(), ...item };
      LEGACY_ROW_KEYS.forEach(key => { delete row[key]; });
      row.rowErrors = {};
      row.columnOptions = {};
      row.columnLoading = {};
      row.columnLoaded = {};
      return row;
    },
    columnsWithDataSource() {
      const columns = this.gridFieldConfig?.columns || {};
      return Object.entries(columns)
        .filter(([, col]) => col?.data_source)
        .map(([name]) => name);
    },
    columnDependencyReady(row, column) {
      const deps = this.columnConfig(column)?.depends_on;
      if (!Array.isArray(deps) || !deps.length) return true;
      this.activate(row, column);
      const ctx = this.fieldCtxS(row);
      return deps.every(dep => formConfigHelper.isFilledValue(ctx.getField(dep)));
    },
    async loadColumnOptions(row, column, { force = false, search, mapItem } = {}) {
      if (!this.columnConfig(column)?.data_source) return;
      if (!this.columnDependencyReady(row, column)) return;
      this.activate(row, column);
      await this.loadSelectFieldOptions(this.gridField, {
        tab: this.tabKey,
        column,
        force,
        search,
        mapItem,
      });
    },
    async loadRowColumnOptions(row, { force = false } = {}) {
      const columns = this.columnsWithDataSource()
        .filter(column => !SEARCH_AS_YOU_TYPE_COLUMNS.has(column));
      await Promise.all(columns.map(column => this.loadColumnOptions(row, column, { force })));
    },
    async reloadDetailArrayColumns(row) {
      const columns = Object.entries(this.gridFieldConfig?.columns || {})
        .filter(([, col]) => col?.data_source?.type === 'detail_array')
        .map(([name]) => name);
      await Promise.all(columns.map(column => this.loadColumnOptions(row, column, { force: true })));
    },
    resetDependentColumns(row, changedColumn) {
      Object.entries(this.gridFieldConfig?.columns || {}).forEach(([name, col]) => {
        if (!Array.isArray(col?.depends_on) || !col.depends_on.includes(changedColumn)) return;
        row.columnOptions[name] = {};
        row.columnLoading[name] = false;
        row.columnLoaded[name] = false;
        row[name] = '';
      });
    },
    applyColumnDefaultValues(row, { changedColumn = null } = {}) {
      const ctx = this.fieldCtxS(row);
      Object.entries(this.gridFieldConfig?.columns || {}).forEach(([name, col]) => {
        const entries = col?.default_value;
        if (!Array.isArray(entries) || !entries.length) return;
        if (!this.isColumnEnabledS(row, name)) return;

        const entry = entries.find(e => !e.when || formConfigHelper.evaluateCondition(e.when, ctx));
        if (!entry) return;

        if (entry.type === 'static') {
          if (!formConfigHelper.isFilledValue(row[name])) row[name] = entry.value;
          return;
        }
        if (entry.type !== 'lookup') return;
        if (!this.isLookupDefaultTriggered(entry, changedColumn)) return;

        const detail = this.materialDetails.get(row);
        if (!detail) return;

        if (entry.source_array) {
          const rows = Array.isArray(detail[entry.source_array]) ? detail[entry.source_array] : [];
          const filter = entry.filter || {};
          const match = rows.find(r =>
            Object.entries(filter).every(([rowKey, ctxField]) =>
              String(this.resolveSourcePath(r, rowKey) ?? '') === String(ctx.getField(ctxField) ?? ''))
          );
          const value = this.resolveSourceValue(match, entry.source_field, entry.join);
          row[name] = value !== undefined ? value : '';
          return;
        }

        const value = this.resolveSourceValue(detail, entry.source_field, entry.join);
        if (value !== undefined) row[name] = value;
      });
    },
    isLookupDefaultTriggered(entry, changedColumn) {
      if (!changedColumn) return false;
      const trigger = entry.trigger;
      const triggers = Array.isArray(trigger) ? trigger : (trigger ? [trigger] : []);
      if (triggers.length) return triggers.includes(changedColumn);
      return changedColumn === 'material_number'
        && Object.values(entry.filter || {}).some(value => String(value).includes('material_number'));
    },
    onColumnShow(row, column) {
      this.activate(row, column);
      this.onFieldOptionShow(this.gridField, this.tabKey, { column });
    },
    async refreshRow(row, changedColumn = null) {
      if (!row) return;
      this.activate(row, changedColumn);

      if (changedColumn) {
        this.resetDependentColumns(row, changedColumn);
        if (!this.readonly) {
          this.columns.forEach(name => {
            if (name !== changedColumn && !this.isColumnEnabledS(row, name)) row[name] = '';
          });
        }
      }

      await this.loadRowColumnOptions(row);
      this.applyColumnDefaultValues(row, { changedColumn });
    },
    onColumnChange(row, column, value) {
      row[column] = value;
      this.refreshRow(row, column);
    },
    // Mass update: baris independent, jadi Add TIDAK diblok baris sebelumnya yang belum
    // lengkap (beda dari Block/Unblock) — user biasanya menambah beberapa baris dulu baru
    // mengisi materialnya.
    addNewItem() {
      this.rows.push(this.emptyRow());
      this.refreshRow(this.rows[this.rows.length - 1]);
    },
    removeRow(index) {
      if (this.rows.length <= 1) return;
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          this.rows.splice(index, 1);
        }
      });
    },

    // ---------------------------------------------------------------------
    // Material number
    // ---------------------------------------------------------------------
    async searchMaterial(event, row) {
      const query = event?.query?.trim();
      this.materialSelected.delete(row);
      if (!query || query.length < 2) return;
      await this.loadColumnOptions(row, 'material_number', {
        force: true,
        search: query,
        mapItem: (item) => ({
          label: materialNumberFormatter.formatMaterialNumber(item.material_number),
          value: materialNumberFormatter.formatMaterialNumber(item.material_number),
          raw: item.material_number,
          short_text: item.short_text || '',
        }),
      });
    },
    async onMaterialNumberSelect(row, event) {
      const option = event?.value ?? event;
      const raw = option?.raw ?? option?.value ?? option;
      row.material_number = option?.value ?? raw;
      row.material_number_raw = raw;
      this.materialSelected.add(row);
      this.materialDetails.delete(row);
      await this.fetchMaterialDetail(row, raw);
      this.refreshRow(row, 'material_number');
    },
    onMaterialClear(row) {
      row.material_number = '';
      row.material_number_raw = '';
      this.materialSelected.delete(row);
      this.materialDetails.delete(row);
      this.refreshRow(row, 'material_number');
    },
    async fetchMaterialDetail(row, materialNumber) {
      try {
        const res = await api(this, advanceSearchService.getMaterialDetail(materialNumber), false);
        const detail = res?.data?.data || res?.data || null;
        if (!detail) return;
        if (row.material_number_raw !== materialNumber) return;
        this.materialDetails.set(row, detail);
        await this.reloadDetailArrayColumns(row);
        this.applyColumnDefaultValues(row, { changedColumn: 'material_number' });
      } catch {
        this.materialDetails.delete(row);
      }
    },

    // ---------------------------------------------------------------------
    // Validasi & payload
    // ---------------------------------------------------------------------
    rowErrorsOf(row) {
      const errors = {};
      this.columns.forEach(column => {
        if (!this.isColumnVisibleForRow(row, column)) return;
        if (!this.isColumnMandatoryS(row, column)) return;
        if (formConfigHelper.isFilledValue(row[column])) return;
        errors[column] = true;
      });
      return errors;
    },
    isRowComplete(row) {
      return Object.keys(this.rowErrorsOf(row)).length === 0;
    },
    validate() {
      const minRows = formConfigHelper.getRowRuleValue(this.formConfig, this.tabKey, this.gridField, 'min_rows');
      let valid = !(typeof minRows === 'number' && this.rows.length < minRows);
      this.rows.forEach(row => {
        row.rowErrors = this.rowErrorsOf(row);
        if (Object.keys(row.rowErrors).length) valid = false;
      });
      return valid;
    },
    // Payload = baris dengan nama kolom config (mu_*), plus material_number pakai nilai mentah.
    // Kolom tampilan (mu_old_*), kolom computed, state visual, dan kolom non-visible dibuang.
    getPayload() {
      return this.rows.map(row => {
        const payload = { ...row };
        payload.material_number = row.material_number_raw || row.material_number;
        delete payload.material_number_raw;
        VISUAL_STATE_KEYS.forEach(key => { delete payload[key]; });
        Object.keys(payload).forEach(key => {
          if (key.startsWith('mu_old_') || SERVER_COMPUTED_KEYS.includes(key)) delete payload[key];
        });
        this.columns.forEach(column => {
          if (!this.isColumnVisibleS(column)) delete payload[column];
        });
        return payload;
      });
    },
  }
};
</script>

<style scoped>
:deep(.centered-column-header) {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

:deep(.p-datatable-thead > tr > th.old-value-col) {
  background-color: #fef7e0 !important;
  color: #8b7d5b !important;
  font-weight: 600 !important;
}

:deep(.old-value-body) {
  background-color: #fef7e0 !important;
}

:deep(.old-value-body .p-inputtext:disabled),
:deep(.old-value-body .p-inputnumber:disabled .p-inputtext),
:deep(.old-value-body input:disabled) {
  background-color: #fef7e0 !important;
  color: #8b7d5b !important;
  font-weight: 500 !important;
  opacity: 1 !important;
}

:deep(.p-inputtext:disabled),
:deep(textarea:disabled),
:deep(.p-select.p-disabled),
:deep(.p-disabled) {
  background-color: #f5f5f5 !important;
  pointer-events: none;
  cursor: not-allowed !important;
}

:deep(.p-datatable-sm .p-datatable-tbody > tr > td) {
  font-size: 0.875rem !important;
  padding: 6px 0.75rem !important;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  white-space: nowrap !important;
}

:deep(.p-datatable .p-datatable-tbody > tr.p-datatable-empty-message > td) {
  border: none !important;
  padding: 0 !important;
}

:deep(.p-select:not(.p-select-editable)) {
  min-height: 38px !important;
  height: 38px !important;
}

:deep(.p-inputtext) {
  font-size: 0.875rem !important;
}
</style>
